/** Canonical state hash (FNV-1a 64) — field order matches the C#/Go reference. */
import { Fnv1a64 } from "../../maths/fnv1a64";
import { LevelSim } from "../levelSim";
import { hashModifiers } from "../modifiers";
import { writeSquadsHash } from "../barracks";
import { hasTimedAbility } from "../../data/enemyAbilities";
import { writeAscensionHash } from "../branches/tick";
import { writeWildPetHash } from "../wildPet";

/**
 * Tower archetypes before TOWERS_V2 (archer … beacon, base). The archetypes appended after them (Mortar, Barracks,
 * Arcane Spire, Hex Totem) join the hash's level and gear lists only when the run owns them, so every run and golden
 * that owns none keeps its hash, and a battle suspended by an older build still proves itself on resume.
 */
export const LEGACY_TOWER_ARCHETYPES = 9;
/** Marks the TOWERS_V2 state blocks (beam ramps, shells in flight) in the hash; written only when present. */
const BEAM_HASH_TAG = 0xbea0;
const SHELL_HASH_TAG = 0x5e11;
/** Marks an enemy's ability state (ENEMIES_V2); written only for an archetype with a timed ability or a summoned imp. */
const ABILITY_HASH_TAG = 0xab1e;

/** Marks the run's rare state and a rally offer (M36); written only on a run with a rare. */
const RARE_HASH_TAG = 0x5a1e;
/** Marks the run's Starfall Wyrm state (M40); written only on a run whose Wyrm stage has come. */
const WYRM_HASH_TAG = 0x3f1e;
const WYRM_OUTCOMES = ["alive", "killed", "fled", "released"] as const;
/** Marks an immune unit's bits (M36); written only for an immune enemy. */
const IMMUNE_HASH_TAG = 0x1a1e;
/** Marks a GearBonus troop bonus in the hash (written only when nonzero); outside any count a gear entry writes. */
const TROOPS_HASH_TAG = 0x7400;

/** Canonical FNV-1a hash of the full simulation state. Field order matches the C# reference. */
export function computeHash(sim: LevelSim): bigint {
  const h = new Fnv1a64();
  h.writeInt(sim.levelNumber);
  const hashed = (i: number) => i < LEGACY_TOWER_ARCHETYPES || sim.owned[i] === true;
  sim.towerLevels.forEach((l, i) => { if (hashed(i)) h.writeInt(l); });
  for (let i = 0; i < sim.gear.length; i++) {
    if (!hashed(i)) continue;
    const g = sim.gear[i];
    h.writeRaw(g.damage.raw); h.writeRaw(g.range.raw); h.writeRaw(g.fireRate.raw); h.writeRaw(g.hp.raw); h.writeRaw(g.points.raw); h.writeInt(g.mythic.length); for (const m of g.mythic) h.writeInt(m);
    // VIP troop strength (docs/SHOP.md §8.5) joins the hash only when a run has it (a tag, then the value): every run
    // without it, every golden among them, keeps its hash.
    if (g.troops !== undefined && !g.troops.isZero()) { h.writeInt(TROOPS_HASH_TAG); h.writeRaw(g.troops.raw); }
  }
  h.writeLong(sim.seed);
  h.writeInt(sim.tick);
  h.writeInt(sim.phase);
  h.writeInt(sim.points);
  h.writeInt(sim.nextOfferAt);
  h.writeInt(sim.offersOpened);
  h.writeInt(sim.offersSinceTowers);
  h.writeInt(sim.offersSinceUpgrade);
  h.writeInt(sim.offersSinceRoad);
  h.writeBool(sim.firstClear);
  h.writeInt(sim.chest === null ? -1 : sim.chest.rarity * 8 + sim.chest.band);
  // Gold, then two zeros where iron and crystal were (DESIGN §9.3, one resource since 2026-09-30): a battle without its
  // chest yet hashes exactly as before, so a suspended battle saved by an older build still proves itself on resume.
  h.writeInt(sim.chest?.gold ?? 0);
  h.writeInt(0);
  h.writeInt(0);
  h.writeInt(sim.baseHp);
  h.writeInt(sim.stage);
  h.writeInt(sim.stagesCleared);
  h.writeInt(sim.kills);
  h.writeInt(sim.leaks);
  h.writeInt(sim.rerollCharges);
  h.writeInt(sim.countdown);
  h.writeInt(sim.spawnCursor);
  h.writeInt(sim.schedule.length); h.writeInt(sim.newestStageFirst);   // overlapping stages (M30 #17 #19) merge schedules
  h.writeInt(sim.openingPathLeft);
  h.writeInt(sim.openingTowerLeft);
  h.writeLong(sim.rng.stateValue);
  h.writeInt(sim.road.roadCells.length);
  for (let i = 0; i < sim.road.roadCells.length; i++) {
    h.writeInt(sim.road.roadCells[i].x);
    h.writeInt(sim.road.roadCells[i].y);
    h.writeInt(sim.road.roadTileKinds[i]);
  }
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    h.writeInt(t.id); h.writeInt(t.archetype); h.writeInt(t.cell.x); h.writeInt(t.cell.y); h.writeInt(t.tier);
    h.writeInt(t.cooldown); h.writeInt(t.priority); h.writeInt(t.kills); h.writeInt(t.disabledTicksLeft);
    if (t.beamTarget !== 0 || t.beamTicks !== 0) { h.writeInt(BEAM_HASH_TAG); h.writeInt(t.beamTarget); h.writeInt(t.beamTicks); }
  }
  if (sim.shells.length > 0) {
    h.writeInt(SHELL_HASH_TAG); h.writeInt(sim.nextShellId);
    for (const s of sim.shells) { h.writeInt(s.id); h.writeInt(s.towerId); h.writeInt(s.ticksLeft); h.writeRaw(s.x.raw); h.writeRaw(s.y.raw); h.writeRaw(s.damage.raw); h.writeRaw(s.radius.raw); if (s.seekId !== 0) h.writeInt(s.seekId); }
  }
  // Modifiers and affixes join the hash only on a run that has them (ResolvedModifiers.active): plain runs keep their goldens.
  const affixed = sim.mods.active;
  if (affixed) hashModifiers(h, sim.mods);
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    if (affixed) h.writeInt(e.affix);
    h.writeInt(e.id); h.writeInt(e.archetype); h.writeRaw(e.hp.raw); h.writeRaw(e.distance.raw);
    h.writeRaw(e.slowFraction.raw); h.writeInt(e.slowTicksLeft); h.writeRaw(e.burnPerTick.raw); h.writeInt(e.burnTicksLeft); h.writeInt(e.shredStacks); h.writeRaw(e.shredArmour.raw); h.writeInt(e.boostTicksLeft); h.writeInt(e.boostCooldownLeft); h.writeInt(e.hitsTaken); h.writeInt(e.tolled ? 1 : 0);
    if (e.summonerId !== 0 || hasTimedAbility(sim.balance.enemies[e.archetype].abilities)) {
      h.writeInt(ABILITY_HASH_TAG); h.writeInt(e.act); h.writeInt(e.actTicks); h.writeInt(e.abilityCooldown); h.writeInt(e.burrowsUsed); h.writeRaw(e.surfaceAt.raw); h.writeInt(e.summonerId);
    }
    if (e.immunity !== 0) { h.writeInt(IMMUNE_HASH_TAG); h.writeInt(e.immunity); }   // M36 immune units: only when present
    h.writeInt(e.burnStackTicks.length);
    for (let i = 0; i < e.burnStackTicks.length; i++) { h.writeInt(e.burnStackSource[i]); h.writeRaw(e.burnStackPerTick[i].raw); h.writeInt(e.burnStackTicks[i]); }
  }
  // M36: a run's rare (sim/rares.ts) joins the hash only once a stage has been rare: every other run keeps its hash.
  if (sim.rareStage > 0) {
    h.writeInt(RARE_HASH_TAG); h.writeInt(sim.rareStage); h.writeInt(sim.rareRun?.enemyId ?? 0); h.writeInt(sim.rareOffersLeft); h.writeInt(sim.rareOfferCountdown);
    h.writeInt(sim.rareChest === null ? -1 : sim.rareChest.rarity);
  }
  // M40: the Wyrm (sim/wyrm.ts) joins the hash only once a stage has been its: every other run keeps its hash.
  if (sim.wyrmStage > 0) {
    const w = sim.wyrmRun;
    h.writeInt(WYRM_HASH_TAG); h.writeInt(sim.wyrmStage); h.writeBool(sim.wyrmChoiceOpen); h.writeInt(sim.rareOffersLeft); h.writeInt(sim.rareOfferCountdown);
    if (w !== null) { h.writeInt(w.enemyId); h.writeInt(w.attempts); h.writeInt(WYRM_OUTCOMES.indexOf(w.outcome)); h.writeRaw(w.hp.raw); h.writeRaw(w.hpLeft.raw); }
  }
  h.writeBool(sim.offerOpen);
  sim.troops.writeHash(h);
  writeSquadsHash(sim, h);
  writeAscensionHash(sim, h);                              // tier 4, once a branch is chosen (sim/branches/tick.ts)
  sim.petSystem?.writeHash(sim, h);                        // Pets v2: only on a run with a pet (sim/petSystem.ts)
  writeWildPetHash(sim, h);                                // Pets v2: only on a run with a wild pet (sim/wildPet.ts)
  if (sim.offerOpen) {
    h.writeInt(sim.offer.id);
    h.writeInt(sim.offer.kind);
    h.writeInt(sim.offer.pickedIndex);
    if (sim.offer.rare) h.writeInt(RARE_HASH_TAG);
    for (const c of sim.offer.cards) { h.writeInt(c.kind); h.writeInt(c.index); }
  }
  return h.value;
}
